import { sendDistribution } from '@mentimeter/errors/sendMetric';
import { SessionStorage } from '@mentimeter/storage';
import type { Telemetry } from '../types';

interface FetchMetrics {
  url: string;
  method: string;
  duration: number;
  success: boolean;
  serviceName: string | undefined;
  environment: string | undefined;
  status?: number;
  errorType?: string;
}

function getUrlFromInput(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return input;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  return input.url;
}

function getMethodFromRequest(
  input: RequestInfo | URL,
  init?: RequestInit,
): string {
  if (input instanceof Request) {
    return input.method;
  }

  if (init?.method) {
    return init.method;
  }

  return 'GET';
}

const instrumentedPaths: string[] = ['/v1/flags:resolve'];

function reportMetrics(metrics: FetchMetrics) {
  // Only report metrics for specific paths
  if (!instrumentedPaths.some((path) => metrics.url.includes(path))) {
    return;
  }

  // Only reports metrics in production
  if (metrics.environment !== 'prod') {
    return;
  }

  sendDistribution({
    name: 'mmjs.confidence.resolve_duration',
    value: metrics.duration,
    tags: [
      `service:${metrics.serviceName || 'unknown'}`,
      `error:${metrics.errorType || 'none'}`,
    ],
  });
}

type FetchErrorType =
  | 'abort' // Request aborted
  | 'network' // Network connectivity issues
  | 'cors' // Cross-origin resource sharing issues
  | 'type_error' // Invalid parameters or URL format
  | 'syntax' // Response parsing problems
  | 'timeout' // Request timeout
  | 'storage' // Storage quota issues
  | 'unknown'; // Default fallback

// Map fetch errors to specific error types for better metrics
function mapFetchError(error: unknown): FetchErrorType {
  if (!(error instanceof Error)) {
    return 'unknown';
  }

  const { name, message } = error;

  if (name === 'AbortError') {
    return 'abort';
  }

  if (name === 'TypeError') {
    if (
      message.includes('NetworkError') ||
      message.includes('Failed to fetch')
    ) {
      return 'network';
    }

    if (message.includes('CORS')) {
      return 'cors';
    }

    return 'type_error';
  }

  if (name === 'SyntaxError') {
    return 'syntax';
  }

  if (name === 'TimeoutError' || message.includes('timeout')) {
    return 'timeout';
  }

  if (message.includes('quota') || message.includes('storage')) {
    return 'storage';
  }

  return 'unknown';
}

export function getInstrumentedFetch({ telemetry }: { telemetry: Telemetry }) {
  return async function (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const startTime = performance.now();

    const url = getUrlFromInput(input);
    const method = getMethodFromRequest(input, init);

    try {
      const response = await fetch(input, init);
      const duration = performance.now() - startTime;

      reportMetrics({
        url,
        method,
        duration: Math.round(duration),
        status: response.status,
        success: true,
        serviceName: telemetry.serviceName,
        environment: telemetry.environment,
      });

      return response;
    } catch (error) {
      const duration = performance.now() - startTime;

      const errorType = mapFetchError(error);
      const lastReportedErrorAt = getLastReportedErrorAt();
      const fiveSecondsAgo = Date.now() - 5_000;

      // Only report errors if they haven't already been reported in the last 5 seconds, which is approximately how long the built in Confidence retry mechanism will retry for.
      if (lastReportedErrorAt < fiveSecondsAgo) {
        reportMetrics({
          url,
          method,
          duration: Math.round(duration),
          success: false,
          errorType,
          serviceName: telemetry.serviceName,
          environment: telemetry.environment,
        });
        recordLastReportedErrorAt();
      }

      throw error;
    }
  };
}

const LAST_REPORTED_ERROR_AT_KEY = 'last-reported-confidence-error-at';

function getLastReportedErrorAt() {
  const lastReportedErrorAt = SessionStorage.getItem(
    LAST_REPORTED_ERROR_AT_KEY,
  );

  if (!lastReportedErrorAt) {
    return 0;
  }

  return parseInt(lastReportedErrorAt, 10);
}

function recordLastReportedErrorAt() {
  SessionStorage.setItem({
    type: 'performance',
    key: LAST_REPORTED_ERROR_AT_KEY,
    value: Date.now().toString(),
  });
}
